import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  EstateTaskInstance,
  useEstateTaskInstance_UserUpdateCustomDeadlineMutation,
} from 'queries';
import * as yup from 'yup';

import FormDateField from 'common/form-date-field';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';

const useStyles = makeStyles(() => ({
  container: {
    padding: '10px',
  },
  actionContainer: {
    paddingTop: '10px',
    paddingBottom: '10px',
    float: 'right',
  },
}));

type Props = {
  estateTaskInstance: EstateTaskInstance;
  // eslint-disable-next-line @typescript-eslint/ban-types
  replaceEstateTaskInstance: Function;
  onClose: () => void;
};

type CustomDeadlineTypeFormValue = {
  customDeadlineDate?: Date;
  customDeadlineMonthsFromDateOfDeath?: number;
};

const schema = yup.object().shape({
  customDeadlineDate: yup.date().optional(),
  customDeadlineMonthsFromDateOfDeath: yup.number().optional(),
});

export default function EstateTaskInstanceCustomDeadlineForm({
  estateTaskInstance,
  replaceEstateTaskInstance,
  onClose,
}: Props) {
  const classes = useStyles();
  const { portalId } = useParams<Record<string, string>>();

  const [userUpdateCustomDeadline, { loading: userUpdateCustomDeadlineExecuting }] =
    useEstateTaskInstance_UserUpdateCustomDeadlineMutation({
      onCompleted: ({ data: updatedEstateTaskInstance }) => {
        replaceEstateTaskInstance(estateTaskInstance, {
          ...estateTaskInstance,
          deadlineDate: updatedEstateTaskInstance.deadlineDate,
          customDeadlineType: updatedEstateTaskInstance.customDeadlineType,
          customDeadlineDate: updatedEstateTaskInstance.customDeadlineDate,
          customDeadlineMonthsFromDateOfDeath:
            updatedEstateTaskInstance.customDeadlineMonthsFromDateOfDeath,
        });
        showToast({
          message: 'Successfully updated custom deadline.',
          severity: NotificationSeverity.SUCCESS,
        });
        onClose();
      },
      onError: (e) => {
        showAlert({
          message: 'Error user updating custom deadline: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const { handleSubmit, formState, control } = useForm<CustomDeadlineTypeFormValue>({
    resolver: yupResolver(schema),
    values: {
      customDeadlineDate: estateTaskInstance.customDeadlineDate || undefined,
      customDeadlineMonthsFromDateOfDeath:
        estateTaskInstance.customDeadlineMonthsFromDateOfDeath || undefined,
    },
  });

  const submitForm = async (formData: CustomDeadlineTypeFormValue) => {
    const { customDeadlineDate, customDeadlineMonthsFromDateOfDeath } = formData;

    userUpdateCustomDeadline({
      variables: {
        input: {
          id: estateTaskInstance.id,
          portalId,
          customDeadlineDate,
          customDeadlineMonthsFromDateOfDeath,
        },
      },
    });
  };

  return (
    <>
      <form onSubmit={handleSubmit(submitForm)}>
        <div className={classes.container}>
          <Controller
            name="customDeadlineDate"
            control={control}
            render={({ field: { onChange, value, name } }) => (
              <FormDateField {...{ value, onChange, name }} label="Custom Deadline Date" />
            )}
          />
          {/* TODO - implement customDeadlineMonthsFromDateOfDeath */}
          <div className={classes.actionContainer}>
            <Button
              type="submit"
              variant="contained"
              size="small"
              disabled={formState.isSubmitting || userUpdateCustomDeadlineExecuting}
            >
              Save
            </Button>
          </div>
        </div>
      </form>
    </>
  );
}
